import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ClaraChat.scss";
import {
  startClaraConversation,
  sendClaraAuthMessage,
} from "../../utils/APIs";

const ClaraChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [isInitializing, setIsInitializing] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Location not available:", error);
          // Default to Santiago, Chile
          setLocation({ latitude: -33.4489, longitude: -70.6693 });
        }
      );
    } else {
      setLocation({ latitude: -33.4489, longitude: -70.6693 });
    }
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const startConversation = useCallback(async () => {
    if (!location.latitude || isInitializing) return;

    setIsInitializing(true);
    setSessionEnded(false);
    try {
      const response = await startClaraConversation({
        latitude: location.latitude,
        longitude: location.longitude,
        language: navigator.language.startsWith("en") ? "en" : "es",
      });

      const data = response.data;

      if (data.success) {
        setSessionId(data.data.session_id);
        setMessages([
          {
            id: 0,
            role: "assistant",
            content: data.data.welcome_message,
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to start conversation:", error);
      setMessages([
        {
          id: 0,
          role: "assistant",
          content:
            "¡Hola! 👋 Soy Clara, tu asistente virtual. ¿En qué puedo ayudarte hoy?",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsInitializing(false);
    }
  }, [location, isInitializing]);

  // Start conversation when chat opens and location is available
  useEffect(() => {
    if (isOpen && location.latitude && !sessionId && !isInitializing) {
      startConversation();
    }
  }, [isOpen, location, sessionId, isInitializing, startConversation]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");

    // Add user message to UI immediately
    const userMsgId = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: userMsgId,
        role: "user",
        content: userMessage,
        timestamp: new Date(),
      },
    ]);

    setIsLoading(true);

    try {
      const response = await sendClaraAuthMessage({
        session_id: sessionId,
        message: userMessage,
        latitude: location.latitude,
        longitude: location.longitude,
      });

      const data = response.data;

      if (data.success) {
        setMessages((prev) => [
          ...prev,
          {
            id: data.messageId,
            role: "assistant",
            content: data.response,
            intent: data.intent,
            timestamp: new Date(),
          },
        ]);

        // Check if session should end
        if (data.shouldEndSession) {
          setSessionEnded(true);
        }
      } else {
        // Handle error response
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            role: "assistant",
            content: data.response || "Lo siento, hubo un error. Intenta de nuevo.",
            isError: true,
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          role: "assistant",
          content: "😅 Error de conexión. Por favor, intenta de nuevo.",
          isError: true,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    // Convert markdown-style formatting to HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br/>");
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Start a new chat session
  const handleStartNewChat = async () => {
    setSessionId(null);
    setMessages([]);
    setSessionEnded(false);
    setInput("");
    
    // Small delay to ensure state is reset before starting new conversation
    setTimeout(() => {
      startConversation();
    }, 100);
  };

  return (
    <div className="clara-chat-wrapper">
      {/* Floating Button */}
      <button
        className={`clara-fab ${isOpen ? "clara-fab-active" : ""}`}
        onClick={toggleChat}
        aria-label={isOpen ? "Cerrar chat" : "Abrir chat con Clara"}
      >
        {isOpen ? (
          <i className="mdi mdi-close"></i>
        ) : (
          <i className="mdi mdi-robot"></i>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="clara-chat-container">
          <div className="clara-header">
            <div className="clara-header-left">
              <div className="clara-avatar">
                <i className="mdi mdi-robot"></i>
              </div>
              <div className="clara-info">
                <h3>Clara</h3>
                <span className="status">
                  <span className="status-dot"></span>
                  Asistente Virtual
                </span>
              </div>
            </div>
            <button className="clara-close-btn" onClick={toggleChat}>
              <i className="mdi mdi-close"></i>
            </button>
          </div>

          <div className="clara-messages">
            {isInitializing ? (
              <div className="clara-initializing">
                <div className="clara-spinner"></div>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.role} ${msg.isError ? "error" : ""}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="message-avatar">
                        <i className="mdi mdi-robot"></i>
                      </div>
                    )}
                    <div className="message-bubble">
                      <div
                        className="message-content"
                        dangerouslySetInnerHTML={{
                          __html: formatMessage(msg.content),
                        }}
                      />
                      <span className="message-time">
                        {msg.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="message assistant loading">
                    <div className="message-avatar">
                      <i className="mdi mdi-robot"></i>
                    </div>
                    <div className="message-bubble">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {sessionEnded ? (
            <div className="clara-session-ended">
              <span>La conversación ha finalizado</span>
              <button
                type="button"
                className="clara-new-chat-btn"
                onClick={handleStartNewChat}
              >
                <i className="mdi mdi-refresh"></i>
                Iniciar nueva conversación
              </button>
            </div>
          ) : (
            <form onSubmit={sendMessage} className="clara-input-form">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                disabled={isLoading || isInitializing}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim() || isInitializing}
              >
                <i className="mdi mdi-send"></i>
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default ClaraChat;

